import React, { useState, useRef, useEffect } from 'react';
import AnimatedSessionTitle from './AnimatedSessionTitle';
import PropTypes from 'prop-types';

const StreamingSpinner = ({ isDarkMode }) => (
  <div
    className={`flex-shrink-0 w-3 h-3 rounded-full border-2 border-t-transparent animate-spin ${
      isDarkMode ? 'border-blue-400' : 'border-blue-500'
    }`}
  />
);

StreamingSpinner.propTypes = { isDarkMode: PropTypes.bool };

const PinIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
  </svg>
);

const ChatSessionItem = ({
  id,
  summary,
  isActive,
  onClick,
  onDelete,
  onPin,
  onRename,
  isPinned,
  isDarkMode,
  isStreaming,
  isUnread,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const handleDelete = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onDelete(id, e);
  };

  const handlePin = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onPin(id);
  };

  const handleRenameClick = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    setRenameValue(summary);
    setIsRenaming(true);
  };

  const commitRename = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== summary) {
      onRename(id, trimmed);
    }
    setIsRenaming(false);
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
    else if (e.key === 'Escape') { setIsRenaming(false); }
  };

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setMenuOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handleOutsideClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [menuOpen]);

  const getContainerClasses = () => {
    const base = 'group relative flex items-center justify-between px-2 py-2 rounded-md transition-all';
    const cursor = isRenaming ? 'cursor-default' : 'cursor-pointer';
    if (isDarkMode) {
      return isActive
        ? `${base} ${cursor} bg-[#1e293b] text-white`
        : `${base} ${cursor} text-gray-300 hover:bg-[#1e293b]/50`;
    }
    return isActive
      ? `${base} ${cursor} bg-gray-200 text-gray-900`
      : `${base} ${cursor} text-gray-700 hover:bg-gray-100`;
  };

  const showMenuButton = (isHovered || menuOpen) && !isRenaming;

  const pinIconClass = isDarkMode
    ? isPinned && !isHovered && !menuOpen
      ? 'text-gray-500'
      : 'text-gray-400 hover:text-gray-200'
    : isPinned && !isHovered && !menuOpen
    ? 'text-gray-400'
    : 'text-gray-500 hover:text-gray-700';

  return (
    <div
      className={getContainerClasses()}
      onClick={isRenaming ? undefined : onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={isRenaming ? undefined : summary}
    >
      {/* Unread indicator */}
      {isUnread && !isActive && !isRenaming && (
        <span
          className={`flex-shrink-0 w-1.5 h-1.5 rounded-full mr-1.5 ${
            isDarkMode ? 'bg-blue-400' : 'bg-blue-500'
          }`}
        />
      )}

      {/* Title or inline rename input */}
      {isRenaming ? (
        <input
          ref={inputRef}
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={handleRenameKeyDown}
          onBlur={commitRename}
          onClick={(e) => e.stopPropagation()}
          className={`text-sm flex-1 min-w-0 bg-transparent border-b outline-none ${
            isDarkMode
              ? 'text-white border-gray-500 placeholder-gray-500'
              : 'text-gray-900 border-gray-400 placeholder-gray-400'
          }`}
        />
      ) : (
        <span className="text-sm truncate flex-1 min-w-0 pr-1">
          <AnimatedSessionTitle title={summary} isActive={isActive} isDarkMode={isDarkMode} />
        </span>
      )}

      {/* Right-side slot: spinner · pin icon · three-dots */}
      {!isRenaming && (
        <div className="flex items-center flex-shrink-0" ref={menuRef}>
          {isStreaming && !isActive && !isHovered && !menuOpen && (
            <StreamingSpinner isDarkMode={isDarkMode} />
          )}

          {/* Pin icon — only rendered when pinned, keeps it out of tab order when hidden */}
          {isPinned && (
            <button
              onClick={handlePin}
              className={`p-1 rounded transition-colors ${pinIconClass} ${
                isDarkMode ? 'hover:bg-[#334155]' : 'hover:bg-gray-300'
              }`}
              title="Unpin chat"
            >
              <PinIcon className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Three-dots button */}
          <div className="relative flex-shrink-0">
            <div className={`overflow-hidden transition-all duration-150 ${showMenuButton ? 'w-6' : 'w-0'}`}>
              <button
                onClick={handleMenuToggle}
                className={`p-1 rounded transition-colors ${
                  isDarkMode
                    ? 'hover:bg-[#334155] text-gray-400 hover:text-gray-200'
                    : 'hover:bg-gray-300 text-gray-500 hover:text-gray-700'
                }`}
                title="More options"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="5" cy="12" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="19" cy="12" r="1.5" />
                </svg>
              </button>
            </div>

            {menuOpen && (
              <div
                className={`absolute right-0 top-full mt-1 w-36 rounded-md shadow-lg z-50 py-1 ${
                  isDarkMode
                    ? 'bg-[#1e293b] border border-[#334155]'
                    : 'bg-white border border-gray-200'
                }`}
              >
                {/* Rename */}
                <button
                  onClick={handleRenameClick}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors ${
                    isDarkMode
                      ? 'text-gray-300 hover:bg-[#334155]'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Rename
                </button>

                {/* Pin / Unpin */}
                <button
                  onClick={handlePin}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors ${
                    isDarkMode
                      ? 'text-gray-300 hover:bg-[#334155]'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <PinIcon className="w-3.5 h-3.5" />
                  {isPinned ? 'Unpin' : 'Pin'}
                </button>

                {/* Delete */}
                <button
                  onClick={handleDelete}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors ${
                    isDarkMode
                      ? 'text-red-400 hover:bg-[#334155]'
                      : 'text-red-600 hover:bg-gray-100'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

ChatSessionItem.propTypes = {
  id: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPin: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  isPinned: PropTypes.bool,
  isDarkMode: PropTypes.bool,
  isStreaming: PropTypes.bool,
  isUnread: PropTypes.bool,
};

ChatSessionItem.defaultProps = {
  isPinned: false,
  isDarkMode: true,
  isStreaming: false,
  isUnread: false,
};

export default ChatSessionItem;
